import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { Project, UserProjectRoleRole, Attachment } from '@/types'
import { apiProjects, apiUserProjectRoles } from '@/routes'
import UserProjectRole from '../types/UserProjectRole'
import { RecordType } from '@/components/shared/DeleteRecordModal'
import apiSlice, { deleteRecordSuccess } from '@/features/apiSlice'
import { startAppListening } from '@/store/listenerMiddleware'

export enum ProjectType {
  Invited = 'invited',
  Admin = 'admin',
  Managed = 'managed',
}

startAppListening({
  actionCreator: deleteRecordSuccess,
  async effect({ payload: { recordType, recordIdOrSlug, record } }, listenerApi) {
    if (recordType === RecordType.Project) {
      // refresh projects lists
      projectTypesToUpdate.forEach((projectType) => {
        listenerApi.dispatch(
          apiSliceWithProjects.util.updateQueryData('getProjects', projectType, (projects) => (
            projects.filter((project) => project.id !== recordIdOrSlug)
          )),
        )
      })
    } else if (recordType === RecordType.Attachment) {
      const deletedAtt = record as unknown as Attachment
      if (deletedAtt.recordType !== RecordType.Project) return

      listenerApi.dispatch(apiSliceWithProjects.util.updateQueryData('getProject', deletedAtt.recordId, (project) => {
        if (deletedAtt.name === 'recording') {
          project.photos = undefined
        } else {
          const attName = deletedAtt.name as 'attachments' | 'photos'
          project[attName] = project[attName]!.filter((att) => att.id !== deletedAtt.id)
        }
      }))
    }
  },
})

const projectTypesToUpdate = [ProjectType.Admin, ProjectType.Managed]
export const apiSliceWithProjects = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getProjects: builder.query<Project[], ProjectType>({
      query: (projectType) => {
        return projectType === ProjectType.Invited
               ? apiProjects.index()
               : projectType === ProjectType.Managed
                 ? apiProjects.manage()
                 : apiProjects.manage({ query: { admin: true } })
      },
    }),
    getProject: builder.query<Project, number>({
      query: (projectId) => apiProjects.show(projectId),
    }),
    createProject: builder.mutation<Project, FormData>({
      query: (body) => ({
        ...apiProjects.create(),
        body,
      }),
      async onQueryStarted(_body, lifecycleApi) {
        const { data: project } = await lifecycleApi.queryFulfilled
        projectTypesToUpdate.forEach((projectType) => {
          lifecycleApi.dispatch(
            apiSliceWithProjects.util.updateQueryData('getProjects', projectType, (draft) => {
              draft.unshift(project)
            }),
          )
        })
        lifecycleApi.dispatch(
          apiSliceWithProjects.util.updateQueryData('getProject', project.id, () => project),
        )
      },
    }),
    updateProject: builder.mutation <Project, { projectId: number, body: FormData }>({
      query: ({ projectId, body }) => ({ ...apiProjects.update(projectId), body }),
      async onQueryStarted({ projectId }, lifecycleApi) {
        const { data: project } = await lifecycleApi.queryFulfilled
        projectTypesToUpdate.forEach((projectType) => {
          lifecycleApi.dispatch(
            apiSliceWithProjects.util.updateQueryData('getProjects', projectType, (draft) => {
              const existing = draft.find(p => p.id === projectId) as Project
              Object.assign(existing, project)
            }),
          )
        })
        lifecycleApi.dispatch(
          apiSliceWithProjects.util.updateQueryData('getProject', projectId, (draft) => project),
        )
      },
    }),
    toggleProjectBlocked: builder.mutation<Project, { projectId: number, blocked: boolean }>({
      query: ({ projectId, blocked }) => ({
        ...apiProjects.toggleProjectBlocked(projectId),
        body: { blocked },
      }),
      async onQueryStarted({ projectId }, lifecycleApi) {
        const { data: project } = await lifecycleApi.queryFulfilled
        projectTypesToUpdate.forEach((projectType) => {
          lifecycleApi.dispatch(
            apiSliceWithProjects.util.updateQueryData('getProjects', projectType, (draft) => {
              const existing = draft.find(p => p.id === projectId) as Project
              Object.assign(existing, project)
            }),
          )
        })
      },
    }),
    updateUPRRole: builder.mutation<
      UserProjectRole, { userProjectRoleId: number, role: UserProjectRoleRole }
    >({
      query: ({ userProjectRoleId, role }) => ({
        ...apiUserProjectRoles.updateRole(userProjectRoleId),
        body: { role },
      }),
      async onQueryStarted(_args, lifecycleApi) {
        const { data: updatedUpr } = await lifecycleApi.queryFulfilled
        updateProjectsAfterUprChange(updatedUpr, lifecycleApi.dispatch)
      },
    }),
    toggleUPRBlocked: builder.mutation<UserProjectRole, number>({
      query: (userProjectRoleId) => apiUserProjectRoles.toggleBlocked(userProjectRoleId),
      async onQueryStarted(_args, lifecycleApi) {
        const { data: updatedUpr } = await lifecycleApi.queryFulfilled
        updateProjectsAfterUprChange(updatedUpr, lifecycleApi.dispatch)
      },
    }),
  }),
})

function updateProjectsAfterUprChange(
  updatedUpr: UserProjectRole,
  dispatch: ThunkDispatch<any, any, UnknownAction>,
) {
  projectTypesToUpdate.forEach((projectType) => {
    dispatch(
      apiSliceWithProjects.util.updateQueryData('getProjects', projectType, (draft) => {
        const existing = draft.find(p => p.id === updatedUpr.projectId) as Project
        existing.userProjectRoles = existing.userProjectRoles!.map((upr) =>
          upr.id === updatedUpr.id ? updatedUpr : upr,
        )
      }),
    )
  })
}

export const {
  useGetProjectsQuery,
  useGetProjectQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useToggleProjectBlockedMutation,
  useUpdateUPRRoleMutation,
  useToggleUPRBlockedMutation,
} = apiSliceWithProjects
